import { Alert, Grid, Stack, Text } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'

import { AccountsTree } from '@/components/AccountsTree'
import {
  type AccountLookup,
  useAccountLookups,
} from '@/hooks/useAccountLookups'

export const stagingEntities = [
  {
    id: 'foundation',
    name: 'Fission Foundation',
    description: 'Fission Foundation',
  },
  {
    id: 'fission-labs',
    name: 'Fission Labs',
    description: 'Microcapital, Inc. dba Fission Labs',
  },
]

export const stagingAccountLookups: AccountLookup[] = [
  {
    name: 'Foundation',
    description: 'Fission Foundation',
    entityId: 'foundation',
    balances: [
      {
        lookup: 'external_financial',
        id: 'foundation-tenet-usd',
        name: 'Foundation Bank Account',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x14A2e815626158B5f4928288A8758e38FD07389A',
        name: 'Treasury Wallet',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0xA66c154df5CAF97468Eb0a53c9743C151234B64B',
        name: 'Investments Wallet',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x2a3C52ed7f96A556f08a2E52694FCD6b1bD916Ed',
        name: 'Discretionary Rewards Wallet',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0xaF88D59e6630995D221E097b72e28D8cfeC00037',
        name: 'Auto Rewards Wallet',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x58C238a58bb5AB95FBa0c8EA5573074BE68Ff2B0',
        name: 'Ops Gas',
      },
    ],
  },
  {
    name: 'XPE Minter',
    description: 'Fission Technology Ventures Ltd.',
    entityId: 'foundation',
    balances: [
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x2D38908E7A9813d90e9F1d6E4Ad6B16e7676BDE4',
        name: 'XPE Minter Wallet',
      },
    ],
  },
  {
    name: 'XPE Feeder Fund - Cayman',
    description: 'Fission Technology Holdings',
    entityId: 'foundation',
    balances: [
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x1a0b3219FFd7ccb6259D4CbB02dea9f89F395dd8',
        name: 'Coinbase Wallet',
      },
      {
        lookup: 'feeder_master_shares',
        feeder_symbol: 'XPE',
        name: 'Master Shares Holdings',
      },
    ],
  },
  {
    name: 'XPE Master Fund',
    description: 'Fission Technology Limited Ltd.',
    entityId: 'foundation',
    balances: [
      {
        lookup: 'external_financial',
        id: 'xvc-master-tenet-usd',
        name: 'Master Bank Account',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x6C830C0300972311699640030fdfb86eA7b8c98B',
        name: 'Master Wallet',
      },
    ],
  },
  {
    name: 'XPE Vault',
    description: 'Fission Technology Vault Ltd.',
    entityId: 'foundation',
    balances: [
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'contract',
        blockchainAddress: '0x1FCFcea222f5ab2e698C666fDa66D00E33eeb63B',
        name: 'XPE Vault',
      },
    ],
  },
  {
    name: 'Fission Labs',
    // description: 'Microcapital, Inc. dba Fission Labs',
    entityId: 'fission-labs',
    balances: [
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0x9440c38344fE05C6453059eeAE9fde9850DB795D',
        name: 'Fission Labs Wallet',
      },
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0xf6f66f7e406e9B61764ed0FeFD66dac0fB044159',
        name: 'Fission Labs Wallet (CB)',
      },
      {
        lookup: 'external_financial',
        id: 'labs-chase-usd',
        name: 'Fission Labs Bank Account',
      },
    ],
  },
  {
    name: 'Fission Capital',
    description: 'Fission Capital LLC',
    entityId: 'fission-labs',
    balances: [
      {
        lookup: 'eth',
        network: 'sepolia',
        provider: 'fireblocks',
        blockchainAddress: '0xBDC60aBfC7844D89955ad84e530c1d82f97c7501',
        name: 'Fission Capital Wallet',
      },
    ],
  },
  {
    name: 'XPE Feeder Fund - US',
    description: 'Fission Technology Holdings LP',
    entityId: 'fission-labs',
    balances: [
      {
        lookup: 'zero',
        name: 'US Feeder Wallet',
      },
      {
        lookup: 'feeder_master_shares',
        feeder_symbol: 'XPE',
        name: 'Master Shares Holdings',
      },
      {
        lookup: 'external_financial',
        id: 'labs-feeder-usd',
        name: 'US Feeder Bank Account',
      },
    ],
  },
]

export function Overview() {
  const { accountNodes, isLoading, isFetching } = useAccountLookups({
    lookups: stagingAccountLookups,
    entities: stagingEntities,
    refetchInterval: 15_000,
  })

  return (
    <Stack>
      {isLoading ? (
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Loading Data"
          color="blue"
        >
          <Text size="sm">
            Fetching account balances from the blockchain...
          </Text>
        </Alert>
      ) : (
        <Grid gutter="md">
          <Grid.Col span={12}>
            <AccountsTree
              data={accountNodes}
              mode="tree"
              isFetching={isFetching}
            />
          </Grid.Col>

          {/*/!* Accounts Overview - Full width *!/*/}
          {/*<Grid.Col span={12}>*/}
          {/*  <AccountsOverview />*/}
          {/*</Grid.Col>*/}

          {/*/!* Vault Overview - Full width *!/*/}
          {/*<Grid.Col span={12}>*/}
          {/*  <VaultOverview />*/}
          {/*</Grid.Col>*/}
        </Grid>
      )}
    </Stack>
  )
}
